import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom';
import { usePayForms } from '../hooks/usePayForms';
import { useShipForms } from '../hooks/useShipForms';
import { useTranslation } from 'react-i18next';
import { useCart, useCartTotal, useCartDiscount } from '../stores/cartStore';
import { useMoney } from '../stores/currenciesStore';
import { useForm } from '../hooks/form';
import { useShipmentCalculation, useProvinceAndMunicipality } from '../hooks/useShipmentCalculation';
import { useOrderResponse } from '../hooks/useOrderResponse';
import { useAuth } from '../contexts/AuthContext';
import { useBeneficiaries } from '../hooks/useBeneficiaries';
import FormInput from './FormInput';
import FormSelect from './FormSelect';
import QrModal from './QrModal';
import BeneficiaryModal from './BeneficiaryModal';
import ConfirmationModal from './ConfirmationModal';
import Pagination from './Pagination';
import toast from 'react-hot-toast';

const Checkout = () => {
    const [shipmentPrice, setShipmentPrice] = useState(null)
    const [shouldScrollToError, setShouldScrollToError] = useState(false)
    const [isBeneficiaryModalOpen, setIsBeneficiaryModalOpen] = useState(false)
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
    const [beneficiaryToDelete, setBeneficiaryToDelete] = useState(null)
    const [selectedBeneficiary, setSelectedBeneficiary] = useState(null)
    const [beneficiaryToEdit, setBeneficiaryToEdit] = useState(null)
    const [isFixed, setIsFixed] = useState(false)
    const [sidebarWidth, setSidebarWidth] = useState(0)
    const [sidebarTop, setSidebarTop] = useState(0)
    
    const sidebarRef = useRef(null)
    const sidebarParentRef = useRef(null)
    const { t } = useTranslation();
    const { user, isAuthenticated } = useAuth();
    const { payForms } = usePayForms()
    const { shipForms } = useShipForms()
    const { items, clearCart } = useCart()
    const [cartTotal, cartTotalFormatted] = useCartTotal()
    const { discount, discountFormatted, subtotalFormatted, totalFormatted } = useCartDiscount()
    const { calculate, activeCalculation, fetchShipmentCalculation } = useShipmentCalculation()
    const { provinces, municipalities, setSelectedProvince } = useProvinceAndMunicipality(activeCalculation)
    const { money } = useMoney()
    const { processOrderResponse, placeOrder, isLoading, qrData, isQrModalOpen, closeQrModal } = useOrderResponse();
    const { 
        beneficiaries, 
        pagination, 
        loading: beneficiariesLoading, 
        addBeneficiary, 
        editBeneficiary,
        deleteBeneficiary,
        handleNextPage, 
        handlePreviousPage, 
        handlePageChange,
        refreshBeneficiaries 
    } = useBeneficiaries();
    const { form, handleOnChange, post, errors } = useForm(
        {
            name: '',
            last_name: '',
            phone: '',
            email: '',
            buyer_address: '',
            beneficiary_name: '',
            beneficiary_lastname: '',
            beneficiary_phone: '',
            beneficiary_ci: '',
            beneficiary_email: '',
            province: '',
            municipality: '',
            address: '',
            payment: '',
            shipping_method: shipForms?.length === 1 ? shipForms[0].id : '',
            beneficiary_id: '',
        }
    )

    const selectedShipForm = shipForms?.find(sf => sf.value === form.shipping_method)
    const showShippingAddress = selectedShipForm?.value === 'home_shipping'

    // Fill buyer info from user data if authenticated
    useEffect(() => {
        if (isAuthenticated && user) {
            // Update only if fields are empty
            if (!form.name) {
                handleOnChange({ target: { name: 'name', value: user.name || '' } });
            }
            if (!form.last_name) {
                handleOnChange({ target: { name: 'last_name', value: user.last_name || '' } });
            }
            if (!form.email) {
                handleOnChange({ target: { name: 'email', value: user.email || '' } });
            }
            if (!form.phone) {
                handleOnChange({ target: { name: 'phone', value: user.phone || '' } });
            }
            if (!form.buyer_address) {
                handleOnChange({ target: { name: 'buyer_address', value: user.address || '' } });
            }
        }
    }, [isAuthenticated, user]);

    useEffect(() => {
        if(form.shipping_method && form.province && form.municipality) {
            const price = calculate({
                province: form.province,
                municipality: form.municipality
            })
            console.log('price', price)
            setShipmentPrice(price)
        }
    }, [form.shipping_method, form.province, form.municipality])

    useEffect(() => {
        if (shouldScrollToError && errors && Object.keys(errors).length > 0) {
            // Get all field names with errors
            const errorFields = Object.keys(errors);
            
            // Find all elements with those names and get their positions
            const errorElements = [];
            errorFields.forEach(fieldName => {
                const element = document.querySelector(`[name="${fieldName}"]`);
                if (element) {
                    errorElements.push({
                        element,
                        top: element.getBoundingClientRect().top + window.scrollY // Get absolute position
                    });
                }
            });

            // Sort by position (closest to top first) and get first element
            if (errorElements.length > 0) {
                errorElements.sort((a, b) => a.top - b.top);
                
                // Scroll to the closest element
                const closestElement = errorElements[0].element;
                closestElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                closestElement.focus();
            }
            
            // Reset the flag after scrolling
            setShouldScrollToError(false);
        }
    }, [shouldScrollToError])

    // Handle beneficiary selection
    const handleBeneficiarySelect = (e) => {
        const beneficiaryId = e.target.value;
        handleOnChange(e);
        
        const selected = beneficiaries.find(b => b.id.toString() === beneficiaryId);
        if (selected) {
            setSelectedBeneficiary(selected);
            handleOnChange({ target: { name: 'beneficiary_name', value: selected.name } });
            handleOnChange({ target: { name: 'beneficiary_lastname', value: selected.last_name } });
            handleOnChange({ target: { name: 'beneficiary_phone', value: selected.phone_number } });
            handleOnChange({ target: { name: 'beneficiary_email', value: selected.email } });
            handleOnChange({ target: { name: 'beneficiary_ci', value: selected.identifier } });
        }
    };
    
    // Handle saving a new beneficiary
    const handleSaveBeneficiary = async (newBeneficiary) => {
        try {
            await refreshBeneficiaries();
            // Auto-select the newly created beneficiary
            setSelectedBeneficiary(newBeneficiary);
            handleOnChange({ target: { name: 'beneficiary_id', value: newBeneficiary.id.toString() } });
            handleOnChange({ target: { name: 'beneficiary_name', value: newBeneficiary.name } });
            handleOnChange({ target: { name: 'beneficiary_lastname', value: newBeneficiary.lastname || newBeneficiary.last_name } });
            handleOnChange({ target: { name: 'beneficiary_phone', value: newBeneficiary.phone_number } });
            handleOnChange({ target: { name: 'beneficiary_email', value: newBeneficiary.email } });
            handleOnChange({ target: { name: 'beneficiary_ci', value: newBeneficiary.identifier } });
            
            // Show success toast message based on whether it was an edit or create operation
            if (beneficiaryToEdit) {
                toast.success(t('checkout.beneficiary_updated_success'));
            } else {
                toast.success(t('checkout.beneficiary_created_success'));
            }
        } catch (error) {
            console.error('Failed to select beneficiary:', error);
            toast.error(t('checkout.beneficiary_save_error'));
        }
    };

    // Handle opening the modal for editing
    const handleEditBeneficiary = (beneficiary) => {
        setBeneficiaryToEdit(beneficiary);
        setIsBeneficiaryModalOpen(true);
    };

    // Handle initiating the delete process
    const handleDeleteBeneficiary = (beneficiary) => {
        setBeneficiaryToDelete(beneficiary);
        setIsConfirmModalOpen(true);
    };

    // Handle confirming the deletion
    const handleConfirmDelete = async () => {
        if (!beneficiaryToDelete) return;
        
        try {
            await deleteBeneficiary(beneficiaryToDelete.id);
            
            // If the deleted beneficiary was selected, clear the selection and all beneficiary data
            if (form.beneficiary_id === beneficiaryToDelete.id.toString()) {
                handleOnChange({ target: { name: 'beneficiary_id', value: '' } });
                handleOnChange({ target: { name: 'beneficiary_name', value: '' } });
                handleOnChange({ target: { name: 'beneficiary_lastname', value: '' } });
                handleOnChange({ target: { name: 'beneficiary_phone', value: '' } });
                handleOnChange({ target: { name: 'beneficiary_email', value: '' } });
                handleOnChange({ target: { name: 'beneficiary_ci', value: '' } });
                setSelectedBeneficiary(null);
            }
            
            toast.success(t('checkout.beneficiary_deleted_success'));
            setIsConfirmModalOpen(false);
            setBeneficiaryToDelete(null);
        } catch (error) {
            console.error('Failed to delete beneficiary:', error);
            toast.error(t('checkout.beneficiary_delete_error'));
        }
    };

    // Handle closing the modals
    const handleCloseModal = () => {
        setBeneficiaryToEdit(null);
        setIsBeneficiaryModalOpen(false);
    };

    const handleCloseConfirmModal = () => {
        setBeneficiaryToDelete(null);
        setIsConfirmModalOpen(false);
    };

    const computedTotal = useMemo(() => {
        if(cartTotal !== undefined && shipmentPrice !== undefined) {
            return money(cartTotal + (shipmentPrice ?? 0))
        }

        return money(0)
    }, [cartTotal, shipmentPrice])

    const handlePlaceOrder = () => {
        placeOrder(post, clearCart).catch((err) => {
            console.log(err, 'errrrrrr');
            
            // Check if it's an error with unavailable products
            if (err.response && err.response.status === 422) {
                const responseData = err.response.data;
                
                // Check if the error contains unavailable products information
                if (responseData && responseData.unavailableProducts) {
                    const unavailableProducts = responseData.unavailableProducts;
                    
                    // Create a message listing the unavailable products
                    const productsText = unavailableProducts.map(product => 
                        product.name ? `${product.name}` : 'Unknown product'
                    ).join(', ');
                    
                    // Show error notification
                    toast.error(t('checkout.unavailable_products', {
                        products: productsText
                    }) || `These products are not available: ${productsText}`);
                }
            }
            
            // Set flag to trigger scrolling in the useEffect
            setShouldScrollToError(true);
        })
    }

    const handleShippingMethodChange = (e) => {
        handleOnChange(e)
        if(e.target.value === 'home_shipping') {
            fetchShipmentCalculation()
        }
    }

    // Check if we're on desktop
    const isDesktop = () => {
        return window.innerWidth >= 768; // lg breakpoint
    }
    
    // Handle scroll for fixed sidebar
    useEffect(() => {
        // Only apply this effect if we have items in cart
        if (!items || items.length === 0) return;
        
        const handleScroll = () => {
            if (!sidebarRef.current || !isDesktop()) return;
            
            // Get parent dimensions from document body if sidebarParentRef is not available
            const parentRect = sidebarParentRef.current ? 
                sidebarParentRef.current.getBoundingClientRect() : 
                document.body.getBoundingClientRect();
            
            const sidebarRect = sidebarRef.current.getBoundingClientRect();
            
            // Update width on every scroll to ensure it's accurate
            const columnEl = document.querySelector('.checkout-sidebar-container');
            if (columnEl) {
                const newWidth = columnEl.getBoundingClientRect().width;
                setSidebarWidth(newWidth);
                console.log("Updating sidebar width:", newWidth);
            }
            
            // Calculate when we should stop the fixed position (at bottom of parent)
            const sidebarHeight = sidebarRect.height;
            const bottomThreshold = parentRect.bottom - sidebarHeight - 20; // 20px buffer
            
            // Log scroll position
            console.log("Scroll Y:", window.scrollY, "Should fix:", window.scrollY > 100);
            
            // Calculate when to make the sidebar fixed
            if (window.scrollY > 200) {
                setIsFixed(true);
                console.log("Setting fixed to TRUE");
                
                // If we're approaching the bottom of the parent container
                if (bottomThreshold <= 80) {
                    // Position it relative to the bottom
                    setSidebarTop(bottomThreshold);
                } else {
                    // Normal fixed position at top
                    setSidebarTop(80);
                }
            } else {
                setIsFixed(false);
                console.log("Setting fixed to FALSE");
            }
        };
        
        // Get initial sizes
        const updateSizes = () => {
            const columnEl = document.querySelector('.checkout-sidebar-container');
            if (columnEl) {
                setSidebarWidth(columnEl.getBoundingClientRect().width);
            }
        };
        
        // Add scroll event listener
        window.addEventListener('scroll', handleScroll);
        
        // Add resize event handler
        const handleResize = () => {
            console.log("Window width:", window.innerWidth, "isDesktop:", isDesktop());
            updateSizes();
            handleScroll();
        };
        window.addEventListener('resize', handleResize);
        
        // Initial update
        updateSizes();
        // Initial check
        handleScroll();
        
        // Debug log
        console.log("Initial state - isFixed:", isFixed, "isDesktop:", isDesktop(), "sidebarWidth:", sidebarWidth);
        
        // Cleanup
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [items]);

    // If cart is empty, show empty cart message
    if (!items || items.length === 0) {
        return (
            <section className="checkout py-80">
                <div className="container container-lg">
                    <div className="py-40 text-center">
                        <div className="mb-24">
                            <i className="text-gray-500 ph-bold ph-shopping-cart-simple" style={{ fontSize: '64px' }}></i>
                        </div>
                        <h3 className="mb-16">{t('checkout.empty_cart')}</h3>
                        <p className="mb-32 text-gray-500">{t('checkout.empty_cart_message')}</p>
                        <Link to="/shop" className="px-32 btn btn-main py-18 rounded-8">
                            {t('checkout.continue_shopping')}
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="checkout py-80">
            {/* QR Modal */}
            <QrModal 
                isOpen={isQrModalOpen} 
                onClose={closeQrModal} 
                qrData={qrData} 
            />
            
            {/* Beneficiary Modal */}
            <BeneficiaryModal
                isOpen={isBeneficiaryModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveBeneficiary}
                beneficiaryToEdit={beneficiaryToEdit}
            />
            
            {/* Confirmation Modal for Delete */}
            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={handleCloseConfirmModal}
                onConfirm={handleConfirmDelete}
                title={t('checkout.delete_beneficiary')}
                message={t('checkout.delete_beneficiary_confirm')}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
            />
            
            <div className="container container-lg">
                {/* <div className="py-20 mb-40 border border-gray-100 rounded-8 px-30">
                    <span className="">
                        Have a coupon?{" "}
                        <Link
                            to="/cart"
                            className="text-gray-900 fw-semibold hover-text-decoration-underline hover-text-main-600"
                        >
                            Click here to enter your code
                        </Link>{" "}
                    </span>
                </div> */}
                <div className="row tw-w-full tw-mx-auto" ref={sidebarParentRef}>
                    <div className="col-xl-9 col-lg-8">
                        <form action="#" className="pe-xl-5">
                            {/* Buyer Information */}
                            <div className={`mb-40 tw-border-dashed tw-border-[1px] tw-rounded tw-p-8 ${errors?.name || errors?.last_name || errors?.phone || errors?.email || errors?.buyer_address ? 'tw-border-red-500' : ''}`}>
                                <h6 className="mb-24 text-lg">{t('checkout.buyer_information')}</h6>
                                <div className="row gy-3">
                                    <div className="col-sm-6 col-xs-6">
                                        <FormInput
                                            label={t('checkout.name')}
                                            value={form.name}
                                            name="name"
                                            onChange={handleOnChange}
                                            errors={errors?.name}
                                            required
                                        ></FormInput>
                                    </div>
                                    <div className="col-sm-6 col-xs-6">
                                        <FormInput
                                            label={t('checkout.last_name')}
                                            value={form.last_name}
                                            name="last_name"
                                            onChange={handleOnChange}
                                            errors={errors?.last_name}
                                            required
                                        ></FormInput>
                                    </div>
                                    <div className="col-sm-6 col-xs-6">
                                        <FormInput
                                            label={t('checkout.phone')}
                                            value={form.phone}
                                            name="phone"
                                            onChange={handleOnChange}
                                            errors={errors?.phone}
                                            required
                                        ></FormInput>
                                    </div>
                                    <div className="col-sm-6 col-xs-6">
                                        <FormInput
                                            label={t('checkout.email')}
                                            value={form.email}
                                            name="email"
                                            onChange={handleOnChange}
                                            errors={errors?.email}
                                            type="email"
                                            required
                                        ></FormInput>
                                    </div>
                                    <div className="col-12">
                                        <FormInput
                                            label={t('checkout.address')}
                                            value={form.buyer_address}
                                            name="buyer_address"
                                            onChange={handleOnChange}
                                            errors={errors?.buyer_address}
                                            required
                                        ></FormInput>
                                    </div>
                                </div>
                            </div>

                            {/* Beneficiary Information */}
                            <div className={`mb-40 tw-border-dashed tw-border-[1px] tw-rounded tw-p-8 ${errors?.beneficiary_id || errors?.beneficiary_name || errors?.beneficiary_lastname || errors?.beneficiary_phone || errors?.beneficiary_email || errors?.beneficiary_ci ? 'tw-border-red-500' : ''}`}>
                                <div className="mb-24 tw-flex tw-flex-col tw-gap-1 tw-text-left">
                                    <div className="tw-flex tw-justify-between tw-items-center">
                                        <h6 className="mb-0 text-lg">{t('checkout.beneficiary_information')}</h6>
                                        {isAuthenticated && (
                                            <button 
                                                type="button" 
                                                className="tw-text-gray-600 hover:tw-text-main-600 tw-bg-transparent tw-border-0 tw-p-0"
                                                onClick={() => {
                                                    setBeneficiaryToEdit(null);
                                                    setIsBeneficiaryModalOpen(true);
                                                }}
                                                title={t('checkout.add_new_beneficiary')}
                                            >
                                                <i className="ph-bold ph-plus-circle tw-text-xl"></i>
                                            </button>
                                        )}
                                    </div>
                                    
                                    {isAuthenticated ? (
                                        <div>
                                            {beneficiaries.length > 0 ? (
                                                <div className="tw-mt-2">
                                                    <div className="tw-mb-2 tw-text-gray-700 tw-text-sm">
                                                        {t('checkout.select_beneficiary')}
                                                        {errors?.beneficiary_id && (
                                                            <span className="tw-text-red-600 tw-text-xs tw-ml-2">{errors.beneficiary_id}</span>
                                                        )}
                                                    </div>
                                                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                                                        {beneficiaries.map(beneficiary => (
                                                            <div 
                                                                key={beneficiary.id} 
                                                                className={`tw-border tw-rounded-md tw-p-3 tw-cursor-pointer tw-transition-all tw-duration-200 hover:tw-border-main-600 tw-relative ${form.beneficiary_id === beneficiary.id.toString() ? 'tw-border-main-600' : 'tw-border-gray-200'}`}
                                                                onClick={() => {
                                                                    const e = { 
                                                                        target: { 
                                                                            name: 'beneficiary_id', 
                                                                            value: beneficiary.id.toString() 
                                                                        } 
                                                                    };
                                                                    handleBeneficiarySelect(e);
                                                                }}
                                                            >
                                                                <div className="tw-flex tw-items-start">
                                                                    <div className="common-check common-radio tw-mb-0 tw-mt-1">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="radio"
                                                                            name="beneficiary_id"
                                                                            id={`beneficiary_${beneficiary.id}`}
                                                                            checked={form.beneficiary_id === beneficiary.id.toString()}
                                                                            value={beneficiary.id.toString()}
                                                                            onChange={handleBeneficiarySelect}
                                                                        />
                                                                    </div>
                                                                    <div className="tw-ml-2 tw-flex-1">
                                                                        <div className="tw-flex tw-items-start tw-justify-between">
                                                                            <div className="tw-font-medium tw-text-gray-800">
                                                                                {beneficiary.name} {beneficiary.last_name}
                                                                            </div>
                                                                            <div className="tw-flex tw-space-x-2">
                                                                                <button
                                                                                    type="button"
                                                                                    className="tw-text-gray-500 hover:tw-text-main-600 tw-bg-transparent tw-border-0 tw-p-0"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleEditBeneficiary(beneficiary);
                                                                                    }}
                                                                                    title={t('checkout.edit_beneficiary')}
                                                                                >
                                                                                    <i className="ph-bold ph-pencil-simple tw-text-lg"></i>
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="tw-text-gray-500 hover:tw-text-red-600 tw-bg-transparent tw-border-0 tw-p-0"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleDeleteBeneficiary(beneficiary);
                                                                                    }}
                                                                                    title={t('checkout.delete_beneficiary')}
                                                                                >
                                                                                    <i className="ph-bold ph-trash tw-text-lg"></i>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        <div className="tw-mt-1 tw-text-sm tw-text-gray-500">
                                                                            <div><strong>ID:</strong> {beneficiary.identifier}</div>
                                                                            <div><strong>{t('checkout.phone')}:</strong> {beneficiary.phone_number}</div>
                                                                            <div><strong>{t('checkout.email')}:</strong> {beneficiary.email}</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    
                                                    {pagination.total > pagination.per_page && (
                                                        <div className="tw-mt-4">
                                                            <Pagination 
                                                                pagination={pagination}
                                                                onNextPage={handleNextPage}
                                                                onPreviousPage={handlePreviousPage}
                                                                onPage={handlePageChange}
                                                                dense
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6 tw-px-4 tw-bg-gray-50 tw-rounded-md tw-mt-3">
                                                    <i className="ph-bold ph-user-circle-plus tw-text-gray-400" style={{ fontSize: '48px' }}></i>
                                                    <p className="tw-mb-3 tw-text-center tw-text-gray-500 tw-mt-2">{t('checkout.empty_beneficiaries')}</p>
                                                    <button 
                                                        type="button"
                                                        onClick={() => {
                                                            setBeneficiaryToEdit(null);
                                                            setIsBeneficiaryModalOpen(true);
                                                        }}
                                                        className="px-24 py-10 btn btn-main rounded-8"
                                                    >
                                                        {t('checkout.add_new_beneficiary')}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <button 
                                            type="button" 
                                            className="border-0 tw-p-0 tw-text-xs tw-px-2 tw-py-1 hover:tw-opacity-70 tw-w-fit tw-rounded tw-bg-gray-500 tw-text-white" 
                                            title={t('checkout.copy_from_buyer')}
                                            onClick={() => {
                                                handleOnChange({ target: { name: 'beneficiary_name', value: form.name } });
                                                handleOnChange({ target: { name: 'beneficiary_lastname', value: form.last_name } });
                                                handleOnChange({ target: { name: 'beneficiary_phone', value: form.phone } });
                                                handleOnChange({ target: { name: 'beneficiary_email', value: form.email } });
                                            }}
                                        >
                                            {t('checkout.copy_from_buyer')}
                                        </button>
                                    )}
                                </div>
                                
                                {!isAuthenticated && (
                                    <div className="row gy-3">
                                        <div className="col-sm-6 col-xs-6">
                                            <FormInput
                                                label={t('checkout.name')}
                                                value={form.beneficiary_name}
                                                name="beneficiary_name"
                                                onChange={handleOnChange}
                                                errors={errors?.beneficiary_name}
                                                required
                                            ></FormInput>
                                        </div>
                                        <div className="col-sm-6 col-xs-6">
                                            <FormInput
                                                label={t('checkout.last_name')}
                                                value={form.beneficiary_lastname}
                                                name="beneficiary_lastname"
                                                onChange={handleOnChange}
                                                errors={errors?.beneficiary_lastname}
                                                required
                                            ></FormInput>
                                        </div>
                                        <div className="col-sm-6 col-xs-6">
                                            <FormInput
                                                label={t('checkout.phone')}
                                                value={form.beneficiary_phone}
                                                name="beneficiary_phone"
                                                onChange={handleOnChange}
                                                errors={errors?.beneficiary_phone}
                                                required
                                            ></FormInput>
                                        </div>
                                        <div className="col-sm-6 col-xs-6">
                                            <FormInput
                                                label={t('checkout.email')}
                                                value={form.beneficiary_email}
                                                name="beneficiary_email"
                                                onChange={handleOnChange}
                                                errors={errors?.beneficiary_email}
                                                type="email"
                                                required
                                            ></FormInput>
                                        </div>
                                        <div className="col-sm-6 col-xs-6">
                                            <FormInput
                                                label={t('checkout.identify_number')}
                                                value={form.beneficiary_ci}
                                                name="beneficiary_ci"
                                                onChange={handleOnChange}
                                                errors={errors?.beneficiary_ci}
                                                required
                                            ></FormInput>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Shipping Method Selection and Address */}
                            <div className={`mb-40 tw-border-dashed tw-border-[1px] tw-rounded tw-p-8 ${errors.shipping_method ? 'tw-border-red-500' : ''}`}>
                                <h6 className="mb-24 text-lg">{t('checkout.shipping_and_delivery')}</h6>
                                <div className="row">
                                    {/* Shipping Methods Column */}
                                    <div className="col-md-6 pe-md-4">
                                        {shipForms && shipForms.length > 1 ? (
                                            <div className="shipping-methods">
                                                {shipForms.map((shipForm) => (
                                                    <div key={shipForm.value}>
                                                        <div className="py-16 mb-0 form-check common-check common-radio">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="shipping_method"
                                                                id={`ship_${shipForm.value}`}
                                                                checked={form.shipping_method === shipForm.value}
                                                                value={shipForm.value}
                                                                onChange={handleShippingMethodChange}
                                                                disabled={shipForm.value === 'local_pickup'}
                                                            />
                                                            <label
                                                                className="form-check-label fw-semibold text-neutral-600"
                                                                htmlFor={`ship_${shipForm.value}`}
                                                            >
                                                                {shipForm.label}
                                                            </label>
                                                        </div>
                                                        {form.shipping_method === shipForm.value && shipForm.description && (
                                                            <div className="px-16 py-24 shipping-item__content rounded-8 bg-main-50 position-relative d-block">
                                                                <p className="text-gray-800">
                                                                    {shipForm.description}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="py-16">
                                                <p className="mb-0 fw-semibold text-neutral-600">
                                                    {shipForms?.[0]?.label}
                                                </p>
                                                {shipForms?.[0]?.description && (
                                                    <div className="px-16 py-24 mt-3 shipping-item__content rounded-8 bg-main-50 position-relative d-block">
                                                        <p className="text-gray-800">
                                                            {shipForms[0].description}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Address Form Column */}
                                    <div className="col-md-6 ps-md-4">
                                        {!form.shipping_method ? (
                                            <div className="d-flex align-items-center justify-content-center h-100">
                                                <p className="mb-0 text-gray-500">
                                                    {t('checkout.select_shipping_first')}
                                                </p>
                                            </div>
                                        ) : showShippingAddress ? (
                                            <div>
                                                <div className="row gy-3">
                                                    <div className="col-sm-6">
                                                        <FormSelect
                                                            label={t('checkout.province')}
                                                            value={form.province}
                                                            name="province"
                                                            onChange={(e) => {
                                                                handleOnChange(e)
                                                                setSelectedProvince(e.target.value)
                                                            }}
                                                            options={provinces}
                                                            errors={errors?.province}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="col-sm-6">
                                                        <FormSelect
                                                            label={t('checkout.municipality')}
                                                            value={form.municipality}
                                                            name="municipality"
                                                            onChange={handleOnChange}
                                                            options={municipalities}
                                                            errors={errors?.municipality}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="col-12">
                                                        <FormInput
                                                            label={t('checkout.address')}
                                                            value={form.address}
                                                            name="address"
                                                            onChange={handleOnChange}
                                                            errors={errors?.address}
                                                            required
                                                        ></FormInput>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : selectedShipForm?.value === 'local_pickup' ? (
                                            <div className="px-16 py-24 rounded-8 bg-main-50">
                                                <p className="mb-0 text-gray-800">
                                                    {t('checkout.local_pickup_wip')}
                                                </p>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            {/* Payment Section */}
                            <div className={`mb-40 tw-border-dashed tw-border-[1px] tw-rounded tw-p-8 ${errors?.payment ? 'tw-border-red-500' : ''}`}>
                                <h6 className="mb-24 text-lg">Datos de pago</h6>
                                <div className="row">
                                    <div className="col-12">
                                        {payForms && payForms.map((payForm) => (
                                            <div key={payForm.id}>
                                                <div className="py-16 mb-0 form-check common-check common-radio">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="payment"
                                                        id={`payment_${payForm.id}`}
                                                        value={payForm.id}
                                                        checked={form.payment == payForm.id}
                                                        onChange={handleOnChange}
                                                    />
                                                    <label
                                                        className="form-check-label fw-semibold text-neutral-600"
                                                        htmlFor={`payment_${payForm.id}`}
                                                    >
                                                        {payForm.name}
                                                    </label>
                                                </div>
                                                {form.payment === payForm.id && payForm.description && (
                                                    <div className="px-16 py-24 payment-item__content rounded-8 bg-main-50 position-relative d-block">
                                                        <p className="text-gray-800">
                                                            {payForm.description}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="col-xl-3 col-lg-4 checkout-sidebar-container tw-px-0">
                        {isFixed && isDesktop() && (
                            <div style={{ height: `${sidebarRef.current?.offsetHeight || 0}px` }}></div>
                        )}
                        <div 
                            className="checkout-sidebar"
                            ref={sidebarRef}
                            style={isFixed && isDesktop() ? {
                                position: 'fixed',
                                top: `${sidebarTop}px`,
                                width: `${sidebarWidth}px`,
                            } : {}}
                        >
                            <div className="px-24 py-20 border border-gray-100 rounded-8">
                                <div className="gap-8 pb-16 mb-16 border-gray-100 border-bottom flex-between">
                                    <span className="text-xl text-gray-900 fw-medium font-heading-two">
                                        {t('checkout.product')}
                                    </span>
                                    <span className="text-xl text-gray-900 fw-medium font-heading-two">
                                        {t('checkout.subtotal')}
                                    </span>
                                </div>
                                <div style={{ maxHeight: '250px', minHeight: `minmax(min-content, 300px)`, overflowY: 'auto' }}>
                                    {
                                        items && items.map((item) => (
                                            <div className="gap-24 mb-32 flex-between" key={item.product.id}>
                                                <div className="gap-12 flex-align">
                                                    <span className="text-gray-900 fw-normal text-md font-heading-two w-144">
                                                        {item.product.name}
                                                    </span>
                                                    <span className="text-gray-900 fw-normal text-md font-heading-two">
                                                        <i className="ph-bold ph-x" />
                                                    </span>
                                                    <span className="text-gray-900 fw-semibold text-md font-heading-two">
                                                        {item.quantity}
                                                    </span>
                                                </div>
                                                <span className="text-gray-900 fw-bold text-md font-heading-two tw-whitespace-nowrap">
                                                    {money(item.product?.computed_price || 0)}
                                                </span>
                                            </div>
                                        ))
                                    }
                                </div>
                                <div className="mt-16 border-gray-100 border-top pt-30">
                                    <div className="gap-8 mb-16 flex-between">
                                        <span className="text-xl text-gray-900 font-heading-two fw-semibold">
                                            Subtotal
                                        </span>
                                        <span className="text-gray-900 font-heading-two text-md fw-bold">
                                            {subtotalFormatted}
                                        </span>
                                    </div>
                                    {
                                        discount && discount.amount > 0 && (
                                            <div className="gap-8 mb-16 flex-between">
                                                <span className="text-xl text-gray-900 font-heading-two fw-semibold d-flex align-items-center">
                                                    {t("cart.discount")}
                                                </span>
                                                <span className="text-main-two-600 font-heading-two text-md fw-bold">
                                                    -{discountFormatted}
                                                </span>
                                            </div>
                                        )
                                    }
                                    {
                                        shipmentPrice && (
                                            <div className="gap-8 mb-16 flex-between">
                                                <span className="text-xl text-gray-900 font-heading-two fw-semibold">
                                                    {t('checkout.shipping')}
                                                </span>
                                                <span className="text-gray-900 font-heading-two text-md fw-bold">
                                                    {money(shipmentPrice)}
                                                </span>
                                            </div>
                                        )
                                    }
                                    <div className="gap-8 mb-0 flex-between">
                                        <span className="text-xl text-gray-900 font-heading-two fw-semibold">
                                            Total
                                        </span>
                                        <span className="text-gray-900 font-heading-two text-md fw-bold">
                                            {computedTotal}
                                        </span>
                                    </div>
                                    {discount && discount.amount > 0 && (
                                        <div className="gap-8 p-12 mt-8 mb-0 border flex-between border-main-two-600 rounded-8">
                                            <span className="text-main-two-600 fw-semibold">
                                                {discount.discount.description || t("cart.discount_applied")}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={handlePlaceOrder}
                                type='submit'
                                className={`mt-20 btn btn-main py-18 w-100 rounded-8 ${isLoading ? 'disabled' : ''}`}
                                disabled={isLoading}
                            >
                                {isLoading ? t('checkout.processing') : t('checkout.place_order')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>

    )
}

export default Checkout